import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "convex/react";
import { useEditorStore } from "@/store/editorStore";
import { useVariablesStore } from "@/store/variablesStore";
import { loadProjectVariables, loadCraftspaceVariables, rowsToVariables, upsertVariable } from "@/services/variablesService";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { newId } from "@/lib/localDb";
import { api } from "../../../convex/_generated/api";

/**
 * Migrates variables from the old Zustand localStorage persist store to SQLite.
 * Runs once per session and clears the old localStorage entry.
 */
async function migrateFromLocalStorage(craftspaceId: string, projectId: string): Promise<void> {
  const LEGACY_KEY = "variables-storage";
  const raw = localStorage.getItem(LEGACY_KEY);
  if (!raw) return;

  try {
    const parsed = JSON.parse(raw) as {
      state?: {
        projectVariables?: Record<string, {
          colors?: Array<{ id: string; name: string; value: string }>;
          sizes?: Array<{ id: string; name: string; value: string }>;
          angles?: Array<{ id: string; name: string; value: number }>;
          fontFamilies?: Array<{ id: string; name: string; value: string; src?: string; format?: string }>;
          icons?: Array<{ id: string; name: string; value: string }>;
        }>;
        craftspaceVariables?: Record<string, {
          colors?: Array<{ id: string; name: string; value: string }>;
          sizes?: Array<{ id: string; name: string; value: string }>;
          angles?: Array<{ id: string; name: string; value: number }>;
          fontFamilies?: Array<{ id: string; name: string; value: string; src?: string; format?: string }>;
          icons?: Array<{ id: string; name: string; value: string }>;
        }>;
      };
    };

    const state = parsed.state;
    if (!state) return;

    const ops: Array<Promise<void>> = [];

    // Migrate project variables
    for (const [pid, vars] of Object.entries(state.projectVariables ?? {})) {
      for (const c of vars.colors ?? []) ops.push(upsertVariable(craftspaceId, c.id || newId(), pid, "color", c.name, c.value));
      for (const s of vars.sizes ?? []) ops.push(upsertVariable(craftspaceId, s.id || newId(), pid, "size", s.name, s.value));
      for (const a of vars.angles ?? []) ops.push(upsertVariable(craftspaceId, a.id || newId(), pid, "angle", a.name, String(a.value)));
      for (const f of vars.fontFamilies ?? []) {
        const meta = f.src || f.format ? { src: f.src, format: f.format } : undefined;
        ops.push(upsertVariable(craftspaceId, f.id || newId(), pid, "font_family", f.name, f.value, meta));
      }
      for (const i of vars.icons ?? []) ops.push(upsertVariable(craftspaceId, i.id || newId(), pid, "icon", i.name, i.value));
    }

    // Migrate craftspace variables (use null projectId for craftspace-scoped)
    for (const [, vars] of Object.entries(state.craftspaceVariables ?? {})) {
      for (const c of vars.colors ?? []) ops.push(upsertVariable(craftspaceId, c.id || newId(), null, "color", c.name, c.value));
      for (const s of vars.sizes ?? []) ops.push(upsertVariable(craftspaceId, s.id || newId(), null, "size", s.name, s.value));
      for (const a of vars.angles ?? []) ops.push(upsertVariable(craftspaceId, a.id || newId(), null, "angle", a.name, String(a.value)));
      for (const f of vars.fontFamilies ?? []) {
        const meta = f.src || f.format ? { src: f.src, format: f.format } : undefined;
        ops.push(upsertVariable(craftspaceId, f.id || newId(), null, "font_family", f.name, f.value, meta));
      }
      for (const i of vars.icons ?? []) ops.push(upsertVariable(craftspaceId, i.id || newId(), null, "icon", i.name, i.value));
    }

    await Promise.all(ops);
    localStorage.removeItem(LEGACY_KEY);
    console.log("[Variables] Migrated from localStorage to SQLite");
  } catch (err) {
    console.warn("[Variables] Failed to migrate from localStorage:", err);
  }
}

/**
 * Provider that loads variables from SQLite into the store and keeps CSS variables in sync.
 */
export const CSSVariablesProvider = ({ children }: { children: React.ReactNode }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const {
    updateCombinedCSSVariables,
    setProjectVariables,
    setCraftspaceVariables,
    projectVariables,
    craftspaceVariables,
  } = useVariablesStore();
  const { project } = useEditorStore();
  const loadedRef = useRef<string>("");
  const projectVarsQuery = useQuery(
    api.variables.listByProject,
    !isElectron() && projectId ? { projectId } : "skip",
  );
  const craftspaceVarsQuery = useQuery(
    api.variables.listByCraftspace,
    !isElectron() && project?.craftspace_id ? { craftspaceId: project.craftspace_id } : "skip",
  );

  useEffect(() => {
    if (!projectId || !project?.craftspace_id) return;

    const key = `${project.craftspace_id}:${projectId}`;
    if (loadedRef.current === key) return;
    loadedRef.current = key;

    const craftspaceId = project.craftspace_id;

    const load = async () => {
      if (!isElectron()) return;

      // One-time migration from old localStorage persist store
      await migrateFromLocalStorage(craftspaceId, projectId);

      const [projVars, wsVars] = await Promise.all([
        loadProjectVariables(craftspaceId, projectId),
        loadCraftspaceVariables(craftspaceId),
      ]);

      setProjectVariables(projectId, projVars);
      setCraftspaceVariables(craftspaceId, wsVars);
    };

    void load();
  }, [projectId, project?.craftspace_id, setProjectVariables, setCraftspaceVariables]);

  useEffect(() => {
    if (isElectron()) return;
    if (projectId && projectVarsQuery) {
      setProjectVariables(projectId, rowsToVariables(projectVarsQuery as any));
    }
  }, [projectId, projectVarsQuery, setProjectVariables]);

  useEffect(() => {
    if (isElectron()) return;
    if (!project?.craftspace_id || !projectId) return;
    const legacy = localStorage.getItem("variables-storage");
    if (!legacy) return;
    const migrate = async () => {
      try {
        const parsed = JSON.parse(legacy) as {
          state?: {
            projectVariables?: Record<string, {
              colors?: Array<{ id: string; name: string; value: string }>;
              sizes?: Array<{ id: string; name: string; value: string }>;
              angles?: Array<{ id: string; name: string; value: number }>;
              fontFamilies?: Array<{ id: string; name: string; value: string; src?: string; format?: string }>;
              icons?: Array<{ id: string; name: string; value: string }>;
            }>;
            craftspaceVariables?: Record<string, {
              colors?: Array<{ id: string; name: string; value: string }>;
              sizes?: Array<{ id: string; name: string; value: string }>;
              angles?: Array<{ id: string; name: string; value: number }>;
              fontFamilies?: Array<{ id: string; name: string; value: string; src?: string; format?: string }>;
              icons?: Array<{ id: string; name: string; value: string }>;
            }>;
          };
        };
        const state = parsed.state;
        if (!state) return;
        const ops: Array<Promise<string>> = [];
        const craftspaceId = project.craftspace_id;
        const projectVars = state.projectVariables?.[projectId];
        if (projectVars) {
          for (const c of projectVars.colors ?? []) ops.push(upsertVariable(craftspaceId, `temp-${newId()}`, projectId, "color", c.name, c.value));
          for (const s of projectVars.sizes ?? []) ops.push(upsertVariable(craftspaceId, `temp-${newId()}`, projectId, "size", s.name, s.value));
          for (const a of projectVars.angles ?? []) ops.push(upsertVariable(craftspaceId, `temp-${newId()}`, projectId, "angle", a.name, String(a.value)));
          for (const f of projectVars.fontFamilies ?? []) {
            const meta = f.src || f.format ? { src: f.src, format: f.format } : undefined;
            ops.push(upsertVariable(craftspaceId, `temp-${newId()}`, projectId, "font_family", f.name, f.value, meta));
          }
          for (const i of projectVars.icons ?? []) ops.push(upsertVariable(craftspaceId, `temp-${newId()}`, projectId, "icon", i.name, i.value));
        }
        const craftspaceVars = state.craftspaceVariables?.[craftspaceId];
        if (craftspaceVars) {
          for (const c of craftspaceVars.colors ?? []) ops.push(upsertVariable(craftspaceId, `temp-${newId()}`, null, "color", c.name, c.value));
          for (const s of craftspaceVars.sizes ?? []) ops.push(upsertVariable(craftspaceId, `temp-${newId()}`, null, "size", s.name, s.value));
          for (const a of craftspaceVars.angles ?? []) ops.push(upsertVariable(craftspaceId, `temp-${newId()}`, null, "angle", a.name, String(a.value)));
          for (const f of craftspaceVars.fontFamilies ?? []) {
            const meta = f.src || f.format ? { src: f.src, format: f.format } : undefined;
            ops.push(upsertVariable(craftspaceId, `temp-${newId()}`, null, "font_family", f.name, f.value, meta));
          }
          for (const i of craftspaceVars.icons ?? []) ops.push(upsertVariable(craftspaceId, `temp-${newId()}`, null, "icon", i.name, i.value));
        }
        if (ops.length > 0) {
          await Promise.all(ops);
          localStorage.removeItem("variables-storage");
        }
      } catch (error) {
        console.warn("[Variables] Failed to migrate from localStorage to Convex:", error);
      }
    };
    void migrate();
  }, [project?.craftspace_id, projectId]);

  useEffect(() => {
    if (isElectron()) return;
    if (project?.craftspace_id && craftspaceVarsQuery) {
      setCraftspaceVariables(project.craftspace_id, rowsToVariables(craftspaceVarsQuery as any));
    }
  }, [project?.craftspace_id, craftspaceVarsQuery, setCraftspaceVariables]);

  useEffect(() => {
    if (projectId && project?.craftspace_id) {
      updateCombinedCSSVariables(project.craftspace_id, projectId);
    }
  }, [
    projectId,
    project?.craftspace_id,
    updateCombinedCSSVariables,
    projectVariables,
    craftspaceVariables,
  ]);

  return <>{children}</>;
};
